import { Button } from "@/components/ui/button";
import { useActiveDocument } from "@/context/active-document";
import { useDocumentFileAction } from "@/action/use-document-file-action";
import { useArtifactUuid } from "@/hooks/use-artifact-uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperclip } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

const LIBRARY_FILE_URL_RE = /\/brain\/api\/v1\/library-files\/([a-f0-9-]+)/;

export const ChatMarkdownImage: React.FC<
  React.ImgHTMLAttributes<HTMLImageElement>
> = ({ src, alt, ...props }) => {
  const { activeEditor } = useActiveDocument();
  const documentUuid = useArtifactUuid("documentUuid");
  const { attachFile } = useDocumentFileAction();
  const fileUuid = src ? LIBRARY_FILE_URL_RE.exec(src)?.[1] : undefined;

  const handleAttach = async () => {
    if (!fileUuid || !documentUuid) return;
    try {
      await attachFile(documentUuid, fileUuid);
    } catch (error) {
      console.error("Failed to attach image:", error);
      toast.error("Could not attach image to document");
      return;
    }
    activeEditor
      ?.chain()
      .focus()
      .setLibraryImage({ src: fileUuid, alt: alt ?? undefined })
      .run();
    toast.success("Image attached to document");
  };

  return (
    <div className="relative flex w-full flex-col items-center gap-2">
      <img src={src} alt={alt} {...props} />
      {fileUuid && documentUuid && activeEditor && (
        <Tooltip>
          <TooltipContent>Attach to current document</TooltipContent>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="outline"
              onClick={handleAttach}
              className="absolute top-2 right-2"
            >
              <FontAwesomeIcon icon={faPaperclip} />
            </Button>
          </TooltipTrigger>
        </Tooltip>
      )}
    </div>
  );
};
